import { ascend, pipe, prop, sort } from 'ramda';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { CMSGameCategory } from '@/api/cms/gameCategories';
import {
  filterByCountries,
  filterByGroupAndDuration,
  filterBySchedule,
} from '@/helpers/cms';
import { UpdatedGameCategory } from '@/helpers/game';
import {
  filterCategoriesByAuth,
  formatCategories,
} from '@/helpers/gameFiltration';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useCommonStore } from '@/store';

export const useGamesFiltration = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const {
    isAuthenticated,
    user,
    countryCode,
    recentGamesIds,
    backEndGameCategories,
    categoriesCMS,
    pendingUser,
    categoriesPending,
    fetchAllCategories,
    fetchRecentGames,
    fetchProviders,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      countryCode: state.countryCode,
      recentGamesIds: state.recentGamesIds,
      backEndGameCategories: state.backEndGameCategories,
      categoriesCMS: state.categoriesCMS,
      pendingUser: state.pendingUser,
      categoriesPending: state.categoriesPending,
      fetchAllCategories: state.fetchAllCategories,
      fetchRecentGames: state.fetchRecentGames,
      fetchProviders: state.fetchProviders,
    }))
  );

  const { isMobileDevice } = useServer();

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders, user]);

  useEffect(() => {
    if (!isAuthenticated || !recentGamesIds.length) return;

    fetchRecentGames(isMobileDevice);
  }, [isAuthenticated, recentGamesIds, isMobileDevice, fetchRecentGames]);

  useEffect(() => {
    if (pendingUser) return;

    fetchAllCategories(language);
  }, [fetchAllCategories, language, pendingUser]);

  useEffect(() => {
    if (
      pendingUser ||
      categoriesPending ||
      !backEndGameCategories ||
      !categoriesCMS
    )
      return;

    const gameCategoriesList = pipe<
      [CMSGameCategory[]],
      CMSGameCategory[],
      UpdatedGameCategory[],
      UpdatedGameCategory[],
      UpdatedGameCategory[],
      UpdatedGameCategory[],
      UpdatedGameCategory[]
    >(
      filterCategoriesByAuth(isAuthenticated),
      formatCategories(isAuthenticated, backEndGameCategories),
      filterBySchedule,
      filterByGroupAndDuration<UpdatedGameCategory>({ user }),
      filterByCountries<UpdatedGameCategory>({ countryCode }),
      sort(ascend(prop('order')))
    )(categoriesCMS);

    useCommonStore.setState({ gameCategoriesList });
  }, [
    isAuthenticated,
    backEndGameCategories,
    t,
    user,
    categoriesCMS,
    countryCode,
    pendingUser,
    categoriesPending,
  ]);
};
